import React from 'react';
import { AppRoutes } from '@/appRoutes';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';

// Beyond this many, the list collapses to "+N more" rather than growing
// the popover to fit every affected automation.
const MAX_LISTED_WORKFLOWS = 3;

export type AffectedWorkflow = {
  id: string;
  name: string;
};

type Props = {
  visible: boolean;
  variableName: string;
  workflows: AffectedWorkflow[];
  onCancel: () => void;
  onContinue: () => void;
};

// Warns before an edit to a shared custom variable reaches automations that
// already reference it. Cancel leaves the edit unsaved; Continue applies it
// anyway - see figma node 41785:149888 for the source design.
export const CustomVariableUsageWarningModal: React.FC<Props> = ({
  visible,
  variableName,
  workflows,
  onCancel,
  onContinue,
}) => {
  const count = workflows.length;
  const listed = workflows.slice(0, MAX_LISTED_WORKFLOWS);
  const remaining = count - listed.length;

  return (
    <CustomDeleteModal
      visible={visible}
      onClose={onCancel}
      onConfirm={onContinue}
      title={`Edit ${variableName}`}
      messageFontSize={16}
      cancelText='Cancel'
      confirmText='Continue'
      confirmColor='var(--text-primary)'
      message={
        <div>
          <p style={{ margin: 0 }}>
            This variable is used in {count} automation{count === 1 ? '' : 's'}.
            Changes apply on their next run and may cause those runs to fail.
          </p>
          <p style={{ margin: '16px 0 8px' }}>Used in ({count})</p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {listed.map((workflow) => (
              <li key={workflow.id}>
                <a
                  href={AppRoutes.LOGIBLOCKS_CANVAS(workflow.id)}
                  target='_blank'
                  rel='noreferrer'
                  style={{
                    color: 'var(--text-primary)',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textUnderlinePosition: 'from-font',
                  }}
                >
                  {workflow.name || 'Untitled automation'}
                </a>
              </li>
            ))}
            {remaining > 0 && <li>+{remaining} more</li>}
          </ul>
        </div>
      }
    />
  );
};

export default CustomVariableUsageWarningModal;
